import { cloneElement } from 'react';
import type { ReactElement } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import {
	CheckIcon,
	EmailIcon,
	FacebookIcon,
	FacebookMessengerIcon,
	InstagramIcon,
	LineIcon,
	LinkIcon,
	NextDoorIcon,
	RedditIcon,
	ShareIcon,
	SmsIcon,
	TikTokIcon,
	TwitterIcon,
	VkIcon,
	WhatsAppIcon,
} from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { type TranslatableLabel, useResolvedLabel } from '#src/app/shared/hooks/i18n';
import {
	type ShareAction as ShareActionType,
	type ShareChannel,
	useShareButtonState,
} from '#src/app/shared/hooks/share';

type Channel = ShareChannel;

const CHANNEL_ICON: Record<Channel, ReactElement<{ className?: string }>> = {
	copy: <LinkIcon />,
	email: <EmailIcon />,
	facebook: <FacebookIcon />,
	facebookMessenger: <FacebookMessengerIcon />,
	instagram: <InstagramIcon />,
	instagramStories: <InstagramIcon />,
	line: <LineIcon />,
	mobileNativeShare: <ShareIcon />,
	nextdoor: <NextDoorIcon />,
	reddit: <RedditIcon />,
	sms: <SmsIcon />,
	tiktok: <TikTokIcon />,
	twitter: <TwitterIcon />,
	vk: <VkIcon />,
	whatsapp: <WhatsAppIcon />,
};

// Brand colours (from the legacy social-* palette) tint only the icon; the label keeps the
// button's default text colour. Utility/monochrome channels (copy, email, native share, X,
// TikTok) are omitted and inherit the default colour.
const CHANNEL_ICON_COLOR: Partial<Record<Channel, string>> = {
	facebook: 'text-[#3A689B]',
	facebookMessenger: 'text-[#0A93FF]',
	instagram: 'text-[#FF0076]',
	instagramStories: 'text-[#FF0076]',
	line: 'text-[#06C755]',
	nextdoor: 'text-[#1b8751]',
	reddit: 'text-[#FF4500]',
	sms: 'text-[#528334]',
	vk: 'text-[#0077FF]',
	whatsapp: 'text-[#25D366]',
};

type ShareButtonProps = {
	buttonConfig: {
		ShareAction: ShareActionType['ShareAction'];
		channel: Channel;
		label: TranslatableLabel;
		successLabel?: TranslatableLabel;
		labelSize?: 'default' | 'small';
	};
	isSelected?: boolean;
	/** The DS button variant. Defaults to `secondary`. */
	variant?: 'primary' | 'secondary';
	/** Extra classes for the button, merged after its own. */
	className?: string;
	onBeforeAction?: () => void | Promise<void>;
};
export const ShareButton: React.FC<ShareButtonProps> = ({
	buttonConfig: { ShareAction, channel, label, successLabel, labelSize },
	isSelected = false,
	variant = 'secondary',
	className,
	onBeforeAction,
}) => {
	const { data, actions } = useShareButtonState({ label, successLabel });
	const buttonLabel = useResolvedLabel(data.buttonLabel);

	const leadingIcon = data.clickedState ? (
		<CheckIcon data-testid="share-button-success-icon" className="text-icon-success" />
	) : (
		cloneElement(CHANNEL_ICON[channel], { className: CHANNEL_ICON_COLOR[channel] })
	);

	return (
		<ShareAction onBeforeAction={onBeforeAction} onAfterAction={actions.afterShare}>
			<Button
				variant={variant}
				size={labelSize === 'small' ? 'small' : 'large'}
				leadingIcon={leadingIcon}
				className={cn('w-full', isSelected && 'border-stroke-selected border-2', className)}
			>
				{buttonLabel}
			</Button>
		</ShareAction>
	);
};
